import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { UseOutletContextProviderInvoice } from './ProviderInvoice';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProviderInvoiceDA } from '../../../../services/admin/provider.invoice.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { Message } from '../../../../models/message.model';
import { legendInvalid, legendIconInvalid } from '../../../../utils/legend.data.util';
import { isValidObjectAttributes } from '../../../../scripts/compare.object.script';
import { modalShow } from '../../../../utils/modal.util';
import { getError, getMessage } from '../../../../languages/translations/response';
import { suffixLegend } from '../../../../scripts/name.legend.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { MediaDA } from '../../../../services/admin/media.service';
import { foreignCurrencyDefault, foreignCurrencyPayload, isForeignCurrencyValid, ForeignCurrencyState } from '../../../../components/element/InputForeignCurrency';

import AppPurchaseFormCreate from '../component/PurchaseFormCreate';
import AppPurchaseFormRequirements from '../component/PurchaseFormRequirements';
import AppPurchaseFormSignatures from '../component/PurchaseFormSignatures';
import AppPurchaseFormVouchers from '../component/PurchaseFormVouchers';
import AppPreloader from '../../../../components/Preloader';
import AppModalProviderInvoiceCreateDraft from '../../../../components/modal/ModalProviderInvoiceCreateDraft';
import AppPurchaseViewDraft from '../component/PurchaseViewDraft';

export interface ProviderInvoiceCreatePageProps {};

let errorResponse: Error, messageResponse: Message;

const ProviderInvoiceCreatePage: React.FunctionComponent<ProviderInvoiceCreatePageProps> = props => {
  const {asideMinimize, setAsideMinimize, setRoute, companyForUser, settingsForCompany, treasuryAccountsForCompany, contactsForCompany, productsForCompany, servicesForCompany} = UseOutletContextProviderInvoice()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fromDocumentId = searchParams.get('from_document')

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [provider, setProvider] = useState({value: '', valid: false, object: null as any})
  const [concerning, setConcerning] = useState({value: '', valid: false})
  const [date, setDate] = useState({value: '', valid: false})
  const [currency, setCurrency] = useState({value: '', valid: false, object: null as any})
  const [requirements, setRequirements] = useState({list: false, value: [] as {type: string, description: string, contents: {detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, unit: string}[]}[], valid: [] as {type: boolean, description: boolean, content: boolean, contents: {detail: boolean, quantity: boolean, price_unit: boolean, tax: boolean, unit: boolean}[]}[]})
  const [vouchers, setVouchers] = useState({list: true, value: [] as {name: string, size: number, type: string, view: string, file: any, id_document_inbox?: string}[], valid: [] as {name: boolean, file: boolean}[]})
  const [signatures, setSignatures] = useState({list: true, value: [] as string[], valid: [] as boolean[]})
  const [foreignCurrency, setForeignCurrency] = useState<ForeignCurrencyState>(foreignCurrencyDefault)
  const [providerInvoiceDraft, setProviderInvoiceDraft] = useState<ProviderInvoice | null>(null)

  const submitProviderInvoiceCreate = async () => {
    setLoadIndicator('on')

    let requerimentsIsValid: boolean = requirements.valid.every((item) => isValidObjectAttributes(item))
    let vouchersIsValid: boolean = vouchers.valid.every((item) => isValidObjectAttributes(item))
    let signaturesIsValid: boolean = signatures.valid.every((item) => (item === true))

    if (companyForUser && provider.valid && concerning.valid && date.valid && currency.valid && requirements.list && vouchers.list && signatures.list && requerimentsIsValid && vouchersIsValid && signaturesIsValid && isForeignCurrencyValid(foreignCurrency)) {
      let companyData = {uuid: companyForUser.uuid, name: companyForUser.name, logo: companyForUser.logo, address: companyForUser.address, phone: companyForUser.phone || '', email: companyForUser.email || '', website: companyForUser.website || '', vat_number: companyForUser.vat_number || ''}
      let voucherFiles = vouchers.value.map((voucher) => ({name: voucher.name, file: voucher.file, id_document_inbox: voucher.id_document_inbox}))

      if (providerInvoiceDraft) {
        await ProviderInvoiceDA.putProviderInvoice(companyForUser.id, providerInvoiceDraft.id, provider.object, concerning.value, date.value, companyForUser.currency, foreignCurrencyPayload(foreignCurrency), requirements.value, voucherFiles, signatures.value).then( (response) => {
          if (response.status === 200) {
            messageResponse = response.data

            GlobalSwal.fire({
              title: getMessage(messageResponse.message, lang.code),
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              navigate('/main/purchase/invoice/list', {replace: true})
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        }).finally(() => {
          setLoadIndicator('off')
        })
      } else {
        await ProviderInvoiceDA.postProviderInvoice(companyForUser.id, provider.object, concerning.value, date.value, companyForUser.currency, foreignCurrencyPayload(foreignCurrency), requirements.value, voucherFiles, signatures.value, companyData, '').then( (response) => {
          if (response.status === 201) {
            messageResponse = response.data

            GlobalSwal.fire({
              title: getMessage(messageResponse.message, lang.code),
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              navigate('/main/purchase/invoice/list', {replace: true})
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        }).finally(() => {
          setLoadIndicator('off')
        })
      }
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!provider.value) legendInvalid('legend-contact-required')
        if (!concerning.value) legendInvalid('legend-concerning-required')
        if (!date.value) legendInvalid('legend-date-required')
        if (!currency.value) legendInvalid('legend-currency-required')
        if (!requirements.value) legendInvalid('legend-requirements-required')
        if (!requerimentsIsValid) {
          for (let i = 0; i < requirements.value.length; i++) {
            if (requirements.value[i].contents.length === 0) {
              legendInvalid(`legend-requirement-contents-required${suffixLegend(i)}`)
            } else {
              for (let j = 0; j < requirements.value[i].contents.length; j++) {
                if (!requirements.value[i].contents[j].detail) legendInvalid(`legend-requirement-content-detail-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].unit) legendInvalid(`legend-requirement-content-unit-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].quantity) legendInvalid(`legend-requirement-content-quantity-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].price_unit) legendInvalid(`legend-requirement-content-price-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].tax_rate) legendInvalid(`legend-requirement-content-tax-required${suffixLegend(i, j)}`)
              }
            }
          }
        }
        if (!vouchersIsValid) {
          for (let i = 0; i < vouchers.value.length; i++) {
            if (!vouchers.value[i].name) legendIconInvalid(`input-voucher-name${suffixLegend(i)}`, `legend-voucher-name-required${suffixLegend(i)}`)
          }
        }
        if (!signaturesIsValid) {
          for (let i = 0; i < signatures.value.length; i++) {
            if (!signatures.value[i]) legendInvalid(`legend-signature-required${suffixLegend(i)}`)
          }
        }
        setLoadIndicator('off')
      })
    }
  }

  useEffect( () => {
    if (providerInvoiceDraft) {
      const loadVouchers = async () => {
        let tempVouchersValue: {name: string, size: number, type: string, view: string, file: any}[] = []
        let tempVouchersValid: {name: boolean, file: boolean}[] = []

        for (let voucher of (providerInvoiceDraft.vouchers ?? [])) {
          const vName = voucher.original_name ?? voucher.name ?? ''
          const vType = voucher.content_type ?? 'application/pdf'
          let vView = ''
          if (voucher.id_document_inbox && companyForUser) {
            const signedUrl = await MediaDA.getDocumentSignedUrl(companyForUser.id, voucher.id_document_inbox)
            if (signedUrl) vView = signedUrl
          } else if (voucher.file) {
            vView = voucher.file
          }
          let newContentValue: {name: string, size: number, type: string, view: string, file: any} = {
            name: vName,
            size: voucher.size ?? 0,
            type: vType,
            view: vView,
            file: vView
          }
          let newContentValid: {name: boolean, file: boolean} = {
            name: true,
            file: true
          }

          tempVouchersValue.push(newContentValue)
          tempVouchersValid.push(newContentValid)
        }

        setVouchers({list: true, value: tempVouchersValue, valid: tempVouchersValid})
      }
      loadVouchers()
    } else if (fromDocumentId && companyForUser) {
      const loadFromDocument = async () => {
        const response = await MediaDA.getDocument(companyForUser.id, fromDocumentId)
        if (response.status !== 200) return
        const doc = response.data?.data ?? response.data
        const signedUrl = await MediaDA.getDocumentSignedUrl(companyForUser.id, fromDocumentId)
        if (!signedUrl) return
        setVouchers({
          list: true,
          value: [{
            name: doc.original_filename ?? doc.name ?? '',
            size: doc.size ?? 0,
            type: doc.content_type ?? doc.mime_type ?? 'application/pdf',
            view: signedUrl,
            file: signedUrl,
            id_document_inbox: fromDocumentId
          }],
          valid: [{ name: true, file: true }]
        })
      }
      loadFromDocument()
    } else {
      setVouchers({list: true, value: [], valid: []})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerInvoiceDraft, fromDocumentId])

  useEffect( () => {
    if (mounted) {
      modalShow('modal-provider-invoice-create-draft')
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.providerInvoices, branch: lang.labels.createProviderInvoice}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && treasuryAccountsForCompany && contactsForCompany && productsForCompany && servicesForCompany
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
            <div className="form">
              <AppPurchaseFormCreate purchase={"invoice"} provider={provider} concerning={concerning} date={date} currency={currency} foreignCurrency={foreignCurrency} setProvider={setProvider} setConcerning={setConcerning} setDate={setDate} setCurrency={setCurrency} setForeignCurrency={setForeignCurrency}></AppPurchaseFormCreate>
              <AppPurchaseFormRequirements date={date.value} currency={companyForUser.currency} requirements={requirements} setRequirements={setRequirements} foreignCurrency={foreignCurrency}></AppPurchaseFormRequirements>
              <AppPurchaseFormVouchers record={providerInvoiceDraft} vouchers={vouchers} setVouchers={setVouchers}></AppPurchaseFormVouchers>
              <AppPurchaseFormSignatures signatures={signatures} setSignatures={setSignatures}></AppPurchaseFormSignatures>
              <div className="d-flex flex-stack flex-wrap flex-md-nowrap bg-light-primary border border-primary border-dashed rounded h-100 p-5">
                <div className="notice d-flex align-items-center mb-5 mb-md-0 me-0 me-md-5">
                  <span className="svg-icon svg-icon-3tx svg-icon-primary me-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                      <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                    </svg>
                  </span>    
                  <div className="d-grid">
                    <span className="text-start text-dark fw-bolder fs-5">{lang.labels.processOfCreatingANewProviderInvoiceCompleted}</span>
                    <span className="text-justify text-muted fw-bold">
                      {lang.labels.clickOn}
                      <span className="text-primary text-lowercase fw-bolder mx-1">{lang.labels.createProviderInvoice}</span>
                      {lang.labels.ifYouWantToSaveTheNewProviderInvoice}
                    </span>
                  </div>
                </div>
                <button className="btn btn-xl btn-primary fw-bolder w-100 w-md-300px" type="button" data-kt-indicator={loadIndicator} onClick={submitProviderInvoiceCreate}>
                  <span className="indicator-label text-uppercase text-nowrap">{lang.labels.createProviderInvoice}</span>
                  <span className="indicator-progress">
                    {lang.labels.pleaseWait}
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                </button>
              </div>
            </div>
          </div>
          <AppPurchaseViewDraft record={providerInvoiceDraft} vouchers={vouchers} asideMinimize={asideMinimize} setAsideMinimize={setAsideMinimize}></AppPurchaseViewDraft>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalProviderInvoiceCreateDraft setAsideMinimize={setAsideMinimize} companyForUser={companyForUser} setProviderInvoiceDraft={setProviderInvoiceDraft}></AppModalProviderInvoiceCreateDraft>
    </div>
  )
}

export default ProviderInvoiceCreatePage;
